import {
  type ReactNode,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";

const INTERSECTION_THRESHOLD = 0.1;

export type ScrollPauseWhenVisibleProps = {
  scrollRootRef: RefObject<HTMLElement | null>;
  enabled: boolean;
  pauseId: string;
  reportPause: (id: string, shouldPause: boolean) => void;
  children: ReactNode;
};

/**
 * Pauses chat auto-scroll while this subtree intersects the chat scroll
 * viewport. Used for in-progress widgets (review checklist, thinking state,
 * streaming assistant message) so growth below does not yank content the user
 * is reading off-screen.
 */
export function ScrollPauseWhenVisible({
  scrollRootRef,
  enabled,
  pauseId,
  reportPause,
  children,
}: ScrollPauseWhenVisibleProps) {
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    setScrollRoot((prev) => {
      const next = scrollRootRef.current;
      return prev === next ? prev : next;
    });
  });

  const { ref, inView } = useInView({
    root: scrollRoot,
    threshold: INTERSECTION_THRESHOLD,
    rootMargin: "0px",
  });

  useEffect(() => {
    if (!enabled) {
      reportPause(pauseId, false);
      return;
    }
    reportPause(pauseId, inView);
    return () => reportPause(pauseId, false);
  }, [enabled, inView, pauseId, reportPause]);

  return (
    <div ref={ref} data-testid={`scroll-pause-zone-${pauseId}`}>
      {children}
    </div>
  );
}
